import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  BookText, Plus, RefreshCcw, Search, ThumbsUp, ThumbsDown, Cloud, HelpCircle,
  X, Save,
} from "lucide-react";
import { api } from "@/lib/api";
import { useWebSocket } from "@/lib/ws";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const VERDICT_STYLE = {
  good_trade: { label: "Good Trade", color: "#10b981", Icon: ThumbsUp },
  mistake:    { label: "Mistake",    color: "#ef4444", Icon: ThumbsDown },
  unlucky:    { label: "Unlucky",    color: "#f59e0b", Icon: Cloud },
  average:    { label: "Average",    color: "#6b7280", Icon: HelpCircle },
};

const OutcomeBadge = ({ outcome }) => {
  const map = {
    win:    { c: "#10b981", b: "rgba(16, 185, 129, 0.15)", label: "WIN" },
    loss:   { c: "#ef4444", b: "rgba(239, 68, 68, 0.15)",  label: "LOSS" },
    manual: { c: "#6b7280", b: "rgba(107, 114, 128, 0.15)", label: "NOTE" },
    flat:   { c: "#6b7280", b: "rgba(107, 114, 128, 0.15)", label: "FLAT" },
  };
  const s = map[outcome] || map.flat;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono font-bold border"
      style={{ background: s.b, borderColor: s.c, color: s.c }}
    >
      {s.label}
    </span>
  );
};

const VerdictBadge = ({ verdict }) => {
  const s = VERDICT_STYLE[verdict] || VERDICT_STYLE.average;
  const { Icon } = s;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono font-bold border"
      style={{ background: `${s.color}22`, borderColor: `${s.color}66`, color: s.color }}
    >
      <Icon className="w-3 h-3" strokeWidth={2.25} /> {s.label}
    </span>
  );
};

const money = (v) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  const s = n >= 0 ? "+" : "";
  return `${s}$${n.toFixed(2)}`;
};

const dtShort = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString([], { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const ManualNoteDialog = ({ open, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    symbol: "EURUSD", direction: "BUY", entry_price: 0, exit_price: "", lot_size: 0.1, pnl: "",
    notes: "", tags: "",
  });

  const save = async () => {
    if (!form.symbol) { toast.error("Symbol required"); return; }
    if (!Number(form.entry_price)) { toast.error("Entry price required"); return; }
    setSaving(true);
    try {
      const payload = {
        symbol: form.symbol.toUpperCase(),
        direction: form.direction,
        entry_price: Number(form.entry_price),
        exit_price: form.exit_price === "" ? null : Number(form.exit_price),
        lot_size: Number(form.lot_size) || 0.01,
        pnl: form.pnl === "" ? null : Number(form.pnl),
        notes: form.notes || "",
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      };
      await api.post("/journal/manual", payload);
      toast.success("Note logged");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-[#0a0e1a] border-[#1f2937] text-[#e5e7eb]" data-testid="manual-note-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl tracking-tight">New Manual Note</DialogTitle>
          <DialogDescription className="text-xs text-[#9ca3af]">
            Log a setup, observation or emotion. Attach an optional screenshot.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="vx-label mb-1 block">Symbol</label>
            <Input
              value={form.symbol}
              onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })}
              data-testid="note-symbol"
              className="bg-[#111827] border-[#1f2937] font-mono text-xs uppercase"
            />
          </div>
          <div>
            <label className="vx-label mb-1 block">Direction</label>
            <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
              <SelectTrigger className="h-9 bg-[#111827] border-[#1f2937] text-xs font-mono" data-testid="note-direction">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0e1a] border-[#1f2937] text-[#e5e7eb]">
                <SelectItem value="BUY">BUY</SelectItem>
                <SelectItem value="SELL">SELL</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="vx-label mb-1 block">Entry price</label>
            <Input
              type="number" step="0.00001" value={form.entry_price}
              onChange={(e) => setForm({ ...form, entry_price: e.target.value })}
              data-testid="note-entry"
              className="bg-[#111827] border-[#1f2937] font-mono text-xs"
            />
          </div>
          <div>
            <label className="vx-label mb-1 block">Exit price <span className="text-[#6b7280] normal-case">(optional)</span></label>
            <Input
              type="number" step="0.00001" value={form.exit_price}
              onChange={(e) => setForm({ ...form, exit_price: e.target.value })}
              className="bg-[#111827] border-[#1f2937] font-mono text-xs"
            />
          </div>
          <div>
            <label className="vx-label mb-1 block">Lot size</label>
            <Input
              type="number" step="0.01" value={form.lot_size}
              onChange={(e) => setForm({ ...form, lot_size: e.target.value })}
              className="bg-[#111827] border-[#1f2937] font-mono text-xs"
            />
          </div>
          <div>
            <label className="vx-label mb-1 block">P&L $ <span className="text-[#6b7280] normal-case">(optional)</span></label>
            <Input
              type="number" step="0.01" value={form.pnl}
              onChange={(e) => setForm({ ...form, pnl: e.target.value })}
              className="bg-[#111827] border-[#1f2937] font-mono text-xs"
            />
          </div>
          <div className="col-span-2">
            <label className="vx-label mb-1 block">Tags <span className="text-[#6b7280] normal-case">(comma-sep)</span></label>
            <Input
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              placeholder="a_plus_setup, news_miss, revenge_trade"
              className="bg-[#111827] border-[#1f2937] text-xs"
              data-testid="note-tags"
            />
          </div>
          <div className="col-span-2">
            <label className="vx-label mb-1 block">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={4}
              data-testid="note-body"
              className="w-full bg-[#111827] border border-[#1f2937] rounded p-2 text-xs text-[#e5e7eb]"
              placeholder="What did you see? What did you do? How did it feel?"
            />
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={onClose}
            className="h-9 px-4 rounded border border-[#1f2937] hover:bg-[#1f2937] text-sm text-[#9ca3af] flex items-center gap-1"
            data-testid="cancel-note-btn"
          >
            <X className="w-3.5 h-3.5" /> Cancel
          </button>
          <button
            onClick={save} disabled={saving}
            data-testid="save-note-btn"
            className="h-9 px-5 rounded bg-[#3b82f6] hover:bg-[#2563eb] text-white text-sm flex items-center gap-1 disabled:opacity-60"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Note"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EntryDrawer = ({ entry, onClose, onRegen, regenerating }) => {
  if (!entry) return null;
  const pm = entry.ai_postmortem || {};
  return (
    <Sheet open={!!entry} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        className="w-full sm:w-[520px] max-w-full bg-[#0a0e1a] border-[#1f2937] text-[#e5e7eb] p-0 flex flex-col"
        data-testid="entry-drawer"
      >
        <SheetHeader className="p-5 border-b border-[#1f2937]">
          <SheetTitle className="font-heading text-xl tracking-tight flex items-center gap-2">
            {entry.symbol} · {entry.direction}
            <OutcomeBadge outcome={entry.outcome} />
          </SheetTitle>
          <div className="text-xs text-[#6b7280] font-mono">
            {entry.entry_type === "manual_note" ? "Manual note" : "Auto-logged trade"} · {dtShort(entry.closed_at || entry.created_at)}
          </div>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <div className="grid grid-cols-2 gap-3 text-xs font-mono">
            <div>
              <div className="text-[#6b7280] uppercase tracking-wider text-[10px]">Entry</div>
              <div className="font-bold mt-0.5">{entry.entry_price ?? "—"}</div>
            </div>
            <div>
              <div className="text-[#6b7280] uppercase tracking-wider text-[10px]">Exit</div>
              <div className="font-bold mt-0.5">{entry.exit_price ?? "—"}</div>
            </div>
            <div>
              <div className="text-[#6b7280] uppercase tracking-wider text-[10px]">Lot</div>
              <div className="font-bold mt-0.5">{entry.lot_size ?? "—"}</div>
            </div>
            <div>
              <div className="text-[#6b7280] uppercase tracking-wider text-[10px]">P&L</div>
              <div
                className="font-bold mt-0.5"
                style={{ color: (entry.pnl || 0) >= 0 ? "#10b981" : "#ef4444" }}
              >
                {money(entry.pnl)}
              </div>
            </div>
            {entry.pattern_type && (
              <>
                <div>
                  <div className="text-[#6b7280] uppercase tracking-wider text-[10px]">Pattern</div>
                  <div className="font-bold mt-0.5">{entry.pattern_type}</div>
                </div>
                <div>
                  <div className="text-[#6b7280] uppercase tracking-wider text-[10px]">TF / Session</div>
                  <div className="font-bold mt-0.5">{entry.timeframe || "?"} · {entry.session || "?"}</div>
                </div>
              </>
            )}
          </div>

          {entry.tags?.length > 0 && (
            <div>
              <div className="vx-label mb-1.5">Tags</div>
              <div className="flex flex-wrap gap-1">
                {entry.tags.map((t) => (
                  <span key={t} className="text-[10px] font-mono px-2 py-0.5 rounded bg-[#1f2937] text-[#9ca3af]">
                    #{t}
                  </span>
                ))}
              </div>
            </div>
          )}

          {entry.notes && (
            <div>
              <div className="vx-label mb-1.5">Notes</div>
              <p className="text-xs text-[#e5e7eb] leading-relaxed whitespace-pre-wrap">{entry.notes}</p>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="vx-label">AI Post-Mortem</div>
              <button
                onClick={() => onRegen(entry.id)}
                disabled={regenerating}
                data-testid="regen-postmortem-btn"
                className="h-7 px-2.5 text-[10px] font-mono uppercase tracking-wider rounded border border-[#3b82f6]/40 text-[#3b82f6] hover:bg-[#3b82f6]/10 flex items-center gap-1 disabled:opacity-50"
              >
                <RefreshCcw className={`w-3 h-3 ${regenerating ? "animate-spin" : ""}`} />
                {regenerating ? "Regenerating…" : "Regenerate"}
              </button>
            </div>
            {pm.verdict ? (
              <div className="space-y-3">
                <VerdictBadge verdict={pm.verdict} />
                <p className="text-xs text-[#e5e7eb] leading-relaxed">{pm.summary}</p>
                {pm.lessons?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#6b7280] mb-1.5">Lessons</div>
                    <ul className="space-y-1.5">
                      {pm.lessons.map((l, i) => (
                        <li key={i} className="text-xs text-[#e5e7eb] pl-4 relative before:content-['▸'] before:absolute before:left-0 before:text-[#3b82f6]">
                          {l}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="text-[10px] font-mono text-[#6b7280]">
                  Generated {dtShort(pm.generated_at)}{pm.is_stub ? " · seed sample" : ""}
                </div>
              </div>
            ) : (
              <div className="text-xs text-[#6b7280] font-mono">
                Post-mortem not generated yet.
                {entry.entry_type === "manual_note" && " Manual notes get post-mortems on-demand."}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [pnl, setPnl] = useState("all");
  const [search, setSearch] = useState("");
  const [showNote, setShowNote] = useState(false);
  const [selected, setSelected] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/journal?days=${days}&pnl=${pnl}`);
      setEntries(r.data.entries || []);
    } catch (e) {
      toast.error("Failed to load journal");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [days, pnl]);

  useWebSocket((msg) => {
    if (msg.type === "journal.postmortem.ready") {
      // Refresh silently and, if drawer is open on that entry, refetch it
      load();
      if (selected?.id === msg.data?.entry_id) {
        api.get(`/journal/${msg.data.entry_id}`).then((r) => setSelected(r.data)).catch(() => {});
      }
      toast.success("AI post-mortem ready");
    }
  });

  const filtered = useMemo(() => {
    if (!search) return entries;
    const q = search.toLowerCase();
    return entries.filter((e) =>
      (e.symbol || "").toLowerCase().includes(q) ||
      (e.notes || "").toLowerCase().includes(q) ||
      (e.tags || []).some((t) => t.toLowerCase().includes(q))
    );
  }, [entries, search]);

  const regen = async (id) => {
    setRegenerating(true);
    try {
      const r = await api.post(`/journal/${id}/generate-postmortem`);
      // Refresh selected + list
      const updated = await api.get(`/journal/${id}`);
      setSelected(updated.data);
      load();
      toast.success("Post-mortem regenerated");
    } catch (e) {
      toast.error("Failed to regenerate post-mortem");
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="journal-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="vx-label">Journal</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mt-1">Trade Journal</h1>
          <p className="text-sm text-[#9ca3af] mt-2 max-w-2xl">
            Every closed trade auto-logs here with an AI post-mortem. Manual notes let you log setups,
            emotions or observations that aren&apos;t tied to executed trades.
          </p>
        </div>
        <button
          onClick={() => setShowNote(true)}
          data-testid="new-note-btn"
          className="h-10 px-4 rounded bg-[#3b82f6] hover:bg-[#2563eb] transition-colors font-semibold text-sm flex items-center gap-2 text-white"
        >
          <Plus className="w-4 h-4" /> Manual Note
        </button>
      </div>

      <div className="vx-card p-3 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-[#6b7280]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search symbol, tag or note…"
            data-testid="journal-search"
            className="flex-1 bg-transparent border-0 outline-none text-sm text-[#e5e7eb] placeholder-[#6b7280]"
          />
        </div>
        <Select value={pnl} onValueChange={setPnl}>
          <SelectTrigger className="w-[110px] h-9 bg-[#111827] border-[#1f2937] text-xs font-mono" data-testid="pnl-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0a0e1a] border-[#1f2937] text-[#e5e7eb]">
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="win">Wins</SelectItem>
            <SelectItem value="loss">Losses</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-[130px] h-9 bg-[#111827] border-[#1f2937] text-xs font-mono" data-testid="days-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0a0e1a] border-[#1f2937] text-[#e5e7eb]">
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last 365 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="vx-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[#9ca3af] font-mono text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <BookText className="w-8 h-8 text-[#6b7280] mx-auto mb-2" strokeWidth={1.5} />
            <div className="text-sm text-[#9ca3af]">
              {search ? "No entries match your search." : "No journal entries in this window yet."}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="journal-table">
              <thead className="bg-[#0a0e1a] border-b border-[#1f2937]">
                <tr className="text-left">
                  <th className="py-2 px-3 vx-label">Closed</th>
                  <th className="py-2 px-2 vx-label">Symbol</th>
                  <th className="py-2 px-2 vx-label">Dir</th>
                  <th className="py-2 px-2 vx-label">Pattern</th>
                  <th className="py-2 px-2 vx-label text-right">Entry</th>
                  <th className="py-2 px-2 vx-label text-right">Exit</th>
                  <th className="py-2 px-2 vx-label text-right">P&L</th>
                  <th className="py-2 px-2 vx-label">Outcome</th>
                  <th className="py-2 px-2 vx-label">Post-Mortem</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e, i) => {
                  const pm = e.ai_postmortem || {};
                  const rowClass = e.direction === "BUY" ? "vx-row-buy" : e.direction === "SELL" ? "vx-row-sell" : "";
                  return (
                    <tr
                      key={e.id}
                      onClick={() => setSelected(e)}
                      className={`border-b border-[#1f2937] hover:bg-[#1f2937]/60 transition-colors cursor-pointer ${rowClass}`}
                      data-testid={`journal-row-${i}`}
                    >
                      <td className="py-2.5 px-3 font-mono text-xs text-[#9ca3af]">{dtShort(e.closed_at || e.created_at)}</td>
                      <td className="py-2.5 px-2 font-mono text-xs font-bold">{e.symbol}</td>
                      <td className="py-2.5 px-2 font-mono text-xs">
                        <span style={{ color: e.direction === "BUY" ? "#10b981" : "#ef4444" }}>
                          {e.direction || "—"}
                        </span>
                      </td>
                      <td className="py-2.5 px-2 font-mono text-[10px] text-[#9ca3af]">
                        {e.pattern_type ? `${e.pattern_type}${e.timeframe ? ` · ${e.timeframe}` : ""}` : "—"}
                      </td>
                      <td className="py-2.5 px-2 font-mono text-xs text-right">{e.entry_price ?? "—"}</td>
                      <td className="py-2.5 px-2 font-mono text-xs text-right">{e.exit_price ?? "—"}</td>
                      <td
                        className="py-2.5 px-2 font-mono text-xs text-right font-bold"
                        style={{ color: (e.pnl || 0) >= 0 ? "#10b981" : (e.pnl || 0) < 0 ? "#ef4444" : "#9ca3af" }}
                      >
                        {money(e.pnl)}
                      </td>
                      <td className="py-2.5 px-2"><OutcomeBadge outcome={e.outcome} /></td>
                      <td className="py-2.5 px-2">
                        {pm.verdict ? <VerdictBadge verdict={pm.verdict} /> : (
                          <span className="text-[10px] text-[#6b7280] font-mono">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ManualNoteDialog open={showNote} onClose={() => setShowNote(false)} onSaved={load} />
      <EntryDrawer
        entry={selected}
        onClose={() => setSelected(null)}
        onRegen={regen}
        regenerating={regenerating}
      />
    </div>
  );
}
